import type { FC } from "react";

import { Text, Box, Column, Heading, HtImage } from "@hightouch/htui";

import { Card } from "src/components/card";
import destinationImg from "src/pages/home/assets/Destination.svg";
import audienceImg from "src/pages/home/assets/Audience.svg";
import journeyImg from "src/pages/home/assets/Journey.svg";
import modelImg from "src/pages/home/assets/Model.svg";
import syncImg from "src/pages/home/assets/Sync.svg";
import { useEntitlements } from "src/hooks/use-entitlement";

export type ResourceType =
  | "destination"
  | "audience"
  | "journey"
  | "model"
  | "sync";

type ResourceDefinition = {
  name: string;
  color: string;
  href: string;
  description: string;
  asset: string;
  verb: string;
  gradientColor?: string;
};

type Props = {
  resources: Array<ResourceType>;
  view: "activation" | "marketing";
};
export const ResourceCards: FC<Props> = ({ resources, view }) => {
  const { data: entitlementsData } = useEntitlements(false);

  const ActivationResourceDefinitions: Record<
    "destination" | "model" | "sync",
    ResourceDefinition
  > = {
    destination: {
      name: "Destination",
      color: "indigo",
      asset: destinationImg,
      href: "/destinations/new",
      verb: "Connect a",
      description: "Add your marketing and business tools",
    },
    model: {
      name: "Model",
      color: "warning",
      gradientColor: "warning",
      asset: modelImg,
      href: "/models/new",
      verb: "Define a",
      description: "Query and transform data from your sources",
    },
    sync: {
      name: "Sync",
      color: "green",
      gradientColor: "green",
      asset: syncImg,
      href: "/syncs/new",
      verb: "Create a",
      description: "Send an existing model to a destination",
    },
  };

  const MarketingResourceDefinitions: Record<
    "audience" | "journey" | "destination" | "sync",
    ResourceDefinition
  > = {
    audience: {
      name: "Audience",
      color: "teal",
      gradientColor: "teal",
      asset: audienceImg,
      href: "/audiences/new",
      verb: "Build an",
      description: "Segment customers by attributes and behavior",
    },
    journey: {
      name: "Journey",
      color: "purple",
      asset: journeyImg,
      href: "/journeys/new",
      verb: "Design a",
      description: "Orchestrate experiences across channels",
    },
    destination: {
      name: "Destination",
      color: entitlementsData.entitlements.journeys ? "green" : "indigo",
      gradientColor: entitlementsData.entitlements.journeys
        ? "green"
        : "indigo",
      asset: destinationImg,
      href: "/destinations/new",
      verb: "Connect a",
      description: "Add your marketing and business tools",
    },
    sync: {
      name: "Sync",
      color: "green",
      gradientColor: "green",
      asset: syncImg,
      href: "/syncs/new",
      verb: "Create a",
      description: "Send an existing audience to a destination",
    },
  };

  const ResourceDefinitions =
    view === "activation"
      ? ActivationResourceDefinitions
      : MarketingResourceDefinitions;

  return (
    <Box
      display="grid"
      gap={6}
      flexWrap="wrap"
      gridTemplateColumns="repeat(auto-fit, minmax(320px, 1fr))"
      gridAutoRows="1fr"
    >
      {resources.map((resource) => (
        <ResourceCard
          key={resource}
          {...ResourceDefinitions[resource]}
          view={view}
        />
      ))}
    </Box>
  );
};

const getBackgroundImage = (name: string, view: "activation" | "marketing") => {
  switch (name.toLowerCase()) {
    case "audience":
      return "/assets/home/Audience_Bg.svg";
    case "destination":
      return view === "activation"
        ? "/assets/home/Destination2_Bg.svg"
        : "/assets/home/Destination_Bg.svg";
    case "journey":
      return "/assets/home/Journey_Bg.svg";
    case "model":
      return "/assets/home/Model_Bg.svg";
    case "sync":
      return "/assets/home/Sync_Bg.svg";
    default:
      return null;
  }
};

const ResourceCard = ({
  description,
  color,
  name,
  asset,
  href,
  verb,
  gradientColor,
  view,
}: ResourceDefinition & { view: "activation" | "marketing" }) => {
  const backgroundImage = getBackgroundImage(name, view);
  return (
    <Card
      aria-label={`${verb} ${name}`}
      href={href}
      className="group"
      borderRadius="2xl"
      borderWidth="hairline"
      borderStyle="solid"
      borderColor={`${color}.200`}
      h="100%"
      p={0}
      pos="relative"
      flex={1}
      // Never animate the box-shadow property directly. This can cause major performance issues
      // Read more: https://tobiasahlin.com/blog/how-to-animate-box-shadow/
      _after={{
        content: '""',
        userSelect: "none",
        pos: "absolute",
        borderRadius: "2xl",
        inset: 0,
        boxShadow: "sm",
        opacity: 0,
        transitionProperty: "opacity",
        transitionDuration: "fast",
        transitionTimingFunction: "easeOutQuad",
      }}
      _hover={{
        _after: {
          transitionDuration: "slow",
          opacity: 1,
          borderColor: `${color}.300`,
        },
      }}
    >
      <Box
        boxSize="100%"
        bg="white"
        borderRadius="2xl"
        boxShadow="buttonShadowLight"
        pos="relative"
        overflow="hidden"
        p={6}
      >
        <Box
          aria-hidden="true"
          pos="absolute"
          inset={0}
          borderRadius="2xl"
          overflow="hidden"
          _before={{
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "100%",
            height: "100%",
            background: `radial-gradient(circle at bottom right, var(--chakra-colors-${gradientColor ?? color}-200) -100%, transparent 100%)`,
            transform: [
              "scale(1.5)",
              "scale(1.5)",
              "scale(1.5)",
              "scale(1.5)",
              "scale(1)",
            ],
            transformOrigin: "top right",
            transitionProperty: "transform, filter",
            transitionDuration: "normal",
            transitionTimingFunction: "easeOutQuad",
          }}
          _groupHover={{
            _before: {
              transitionDuration: "slow",
              transform: [
                "scale(1.7)",
                "scale(1.7)",
                "scale(1.7)",
                "scale(1.2)",
              ],
              filter: "brightness(1.15)",
            },
          }}
        />
        {backgroundImage && (
          <img
            src={backgroundImage}
            alt=""
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "right center",
              opacity: 0.6,
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
        )}
        <Column mt="auto" pt={5} align="flex-start">
          <Text color={`${color}.700`}>{verb}</Text>
          <Heading size="xl" color={`${color}.800`}>
            {name}
          </Heading>
          <Text color={`${color}.700`}>{description}</Text>
        </Column>
        <HtImage
          pos="absolute"
          top={-2}
          right={0}
          src={asset}
          h="120px"
          maxW="50%"
          minH="120px"
          minW="120px"
          alt={name}
          ml="auto"
          zIndex={1}
          transitionProperty="transform"
          transitionDuration="faster"
          transitionTimingFunction="easeInOutCubic"
          transformOrigin="top"
          _groupHover={{
            transitionDuration: "normal",
            transform: "scale(1.025)",
          }}
        />
      </Box>
    </Card>
  );
};
